import { GroupedSuggestion } from '@/types/admin'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@brent/core-ui/ui'
import { Badge } from '@brent/core-ui/ui'
import { formatDistanceToNow } from 'date-fns'
import { ExternalLink } from 'lucide-react'

interface SuggestionDetailsDialogProps {
  suggestion: GroupedSuggestion | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SuggestionDetailsDialog({
  suggestion,
  open,
  onOpenChange,
}: SuggestionDetailsDialogProps) {
  if (!suggestion) return null

  const earliestDate = new Date(suggestion.earliest_suggestion)
  const latestDate = new Date(suggestion.latest_suggestion)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{suggestion.title}</DialogTitle>
          <DialogDescription className="text-base">{suggestion.composer}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Wikidata Links */}
          {(suggestion.composer_wikidata_id || suggestion.work_wikidata_id) && (
            <div className="flex flex-wrap gap-3">
              {suggestion.composer_wikidata_id && (
                <a
                  href={`https://www.wikidata.org/wiki/${suggestion.composer_wikidata_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  Composer: {suggestion.composer_wikidata_id}
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {suggestion.work_wikidata_id && (
                <a
                  href={`https://www.wikidata.org/wiki/${suggestion.work_wikidata_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  Work: {suggestion.work_wikidata_id}
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 rounded-lg border bg-muted/50 p-4">
            <div className="text-center">
              <div className="text-2xl font-bold">{suggestion.suggestion_count}</div>
              <div className="text-xs text-muted-foreground">
                {suggestion.suggestion_count === 1 ? 'Suggestion' : 'Suggestions'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium">
                {formatDistanceToNow(earliestDate, { addSuffix: true })}
              </div>
              <div className="text-xs text-muted-foreground">First suggested</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium">
                {formatDistanceToNow(latestDate, { addSuffix: true })}
              </div>
              <div className="text-xs text-muted-foreground">Latest suggestion</div>
            </div>
          </div>

          {/* Suggesters */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Suggested by:</h3>
            <div className="space-y-3">
              {suggestion.suggesters.map((suggester, index) => (
                <div
                  key={index}
                  className="rounded-lg border bg-card p-3 space-y-2 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {suggester.user_name || suggester.user_email || 'Unknown'}
                    </Badge>
                    {suggester.created_at && (
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(suggester.created_at), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                  {suggester.comment && (
                    <p className="text-sm text-muted-foreground pl-3 border-l-2 border-primary/20">
                      {suggester.comment}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
